import React from "react";
import clsx from "clsx";
import { IoOpenOutline, IoCheckmarkCircle, IoTimeOutline } from "react-icons/io5";

import styles from "./styles.module.scss";
import { leaveAfterComma } from "../../../utils/leaveAfterComma";
import EmptyNotice from "../EmptyNotice";
import { IconCoinOff } from "@tabler/icons-react";

// Sales of videos (a context modal): summary on top, then one row per sale.
// innerProps.tableData: { headers: [{ value, colSpan }], body: [sale] } — the
// header with colSpan 2 names the earnings column ("Author earnings", "Your
// earnings" …), so the same modal serves admins, researchers and stringers.
const usd = (n) => `$${leaveAfterComma(Number(n) || 0, 2)}`;

const SaleTableModal = ({ innerProps }) => {
  const headers = innerProps?.tableData?.headers || [];
  const body = innerProps?.tableData?.body || [];
  const ownLabel = headers.find((h) => h.colSpan === 2)?.value || "Earnings";
  const yours = /your/i.test(ownLabel);

  if (!body.length) {
    return (
      <div className={styles.empty}>
        <EmptyNotice icon={<IconCoinOff size="25px" color="black" />} text="No sales here yet" color="black" />
      </div>
    );
  }

  const total = body.reduce((a, x) => a + (Number(x.earnings?.total) || 0), 0);
  const own = body.reduce((a, x) => a + (Number(x.earnings?.own) || 0), 0);
  const unpaid = body.filter((x) => !x.received).reduce((a, x) => a + (Number(x.earnings?.own) || 0), 0);
  const count = body.reduce((a, x) => a + (Number(x.countSales) || 1), 0);
  // sales of one video (admin → payments): the video once on top, not in every row
  const oneVideo = body.every((x) => x.videoId === body[0].videoId) ? body[0] : null;

  return (
    <div className={styles.modal}>
      {oneVideo && (
        <a className={styles.videoHead} href={`/library/${oneVideo.videoId}`} target="_blank" rel="noopener noreferrer">
          <span>{oneVideo.videoTitle}</span>
          <small>
            VB{oneVideo.videoId} <IoOpenOutline />
          </small>
        </a>
      )}
      <div className={styles.summary}>
        <div>
          <small>Sales</small>
          <b>{count}</b>
        </div>
        <div>
          <small>Total earned</small>
          <b>{usd(total)}</b>
        </div>
        <div className={clsx(yours && styles.accent)}>
          <small>{ownLabel}</small>
          <b>{usd(own)}</b>
        </div>
        <div>
          <small>Not received yet</small>
          <b className={unpaid > 0 ? styles.warn : undefined}>{usd(unpaid)}</b>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Company</th>
              {!oneVideo && <th>Video</th>}
              <th>Researchers</th>
              <th className={styles.num}>Sales</th>
              <th className={styles.num}>Total</th>
              <th className={clsx(styles.num, yours && styles.accentHead)}>{ownLabel}</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {body.map((x, i) => (
              <tr key={i}>
                <td className={styles.date}>{x.date}</td>
                <td>
                  <span className={styles.company}>{x.company}</span>
                </td>
                {!oneVideo && (
                <td className={styles.videoCell}>
                  <a href={`/library/${x.videoId}`} target="_blank" rel="noopener noreferrer" title={x.videoTitle}>
                    <span className={styles.videoTitle}>{x.videoTitle}</span>
                    <span className={styles.videoId}>
                      VB{x.videoId} <IoOpenOutline />
                    </span>
                  </a>
                </td>
                )}
                <td className={styles.muted}>{x.researchers || "—"}</td>
                <td className={styles.num}>{x.countSales}</td>
                <td className={styles.num}>{usd(x.earnings?.total)}</td>
                <td className={clsx(styles.num, styles.strong)}>{x.earnings?.own ? usd(x.earnings.own) : "—"}</td>
                <td>
                  {x.received ? (
                    <span className={clsx(styles.pill, styles.pillOk)}>
                      <IoCheckmarkCircle /> Received
                    </span>
                  ) : (
                    <span className={clsx(styles.pill, styles.pillWait)}>
                      <IoTimeOutline /> Not yet
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SaleTableModal;
